/**
 * `/homework/:uploadId` (#376, #573): one upload, as one screen at a time -
 * reading, ready, check, done or failed.
 *
 * - **Reading** says how many pages are done, as numbers, and promises no time
 *   (`reading-progress.tsx`). **Ready** is ONE summary and one Start
 *   (`ready-summary.tsx`), not a card per page. **Check** is the existing
 *   `PageReview`, unchanged, one page at a time with "Page k of n" - the
 *   submission and recording logic is exactly what it was; only what surrounds it
 *   is new. **Done** and **failed** each say one thing and what to do about it.
 * - **Polling stops when the upload settles.** The upload is read in the
 *   background after the POST returns, so this screen asks again every few
 *   seconds while the upload is `processing` or any page is still `reading`,
 *   and not at all once nothing is moving.
 * - **The chosen page is `?page=<pageId>`.** A link rather than component
 *   state, so the browser's back button and a reload both land where the
 *   learner was.
 * - **No percentage.** Counts of pages and lines are words and numbers
 *   (`no-percentage.test.tsx`); a stage is always said in words as well.
 * - **A failed read can be read again (#395).** A page whose readers failed offers
 *   "Read again", which re-runs only the readers that failed; after it, this screen
 *   asks the server again for a short while (`read-again.tsx`).
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useFocusFollows } from '../../app/course-focus.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { HomeworkCrumbs } from './homework-crumbs.js';
import { PageReview } from './page-review.js';
import { ReadySummary } from './ready-summary.js';
import { estimateTimeLeft } from './reading-estimate.js';
import { ReadingProgress } from './reading-progress.js';
import { POLL_INTERVAL_MS } from './polling.js';
import { ReaderFailures } from './reader-failures.js';
import { ReadAgainButton, useReadAgain } from './read-again.js';

export function HomeworkPage(): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const { uploadId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const chosenPageId = searchParams.get('page');
  const readAgain = useReadAgain(uploadId);
  const list = useQuery({ queryKey: queryKeys.homeworkList, queryFn: () => api.listHomework() });
  useFocusFollows(list.data?.uploads.find((item) => item.uploadId === uploadId)?.courseRef);

  const upload = useQuery({
    queryKey: queryKeys.homeworkUpload(uploadId),
    queryFn: () => api.getHomework(uploadId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return false;
      const moving = data.state === 'processing' || data.pages.some((p) => p.reviewStage === 'reading');
      return moving || readAgain.readingAgain ? POLL_INTERVAL_MS : false;
    },
  });

  // The full-page error is for an upload that has never loaded. A failed
  // background read after data arrived must not unmount the review panel below:
  // that would throw away a text box the learner is typing in.
  const data = upload.data;
  if (data === undefined) {
    if (upload.isError) {
      return (
        <div className="page homework">
          <h1>{t.homeworkHeading}</h1>
          <FailureNotice error={upload.error} onRetry={() => void upload.refetch()} />
        </div>
      );
    }
    return (
      <div className="page homework">
        <h1>{t.homeworkHeading}</h1>
        <p role="status">{t.loadingHomework}</p>
      </div>
    );
  }

  const chosen = data.pages.find((page) => page.pageId === chosenPageId);
  const allReaders = data.pages.flatMap((page) => page.readers);
  const anyReaderFailed = allReaders.some((reader) => reader.outcome === 'failed');
  const reading = data.state === 'processing' || data.pages.some((page) => page.reviewStage === 'reading');
  const allConfirmed =
    data.pages.length > 0 &&
    data.pages.length === data.pageCount &&
    data.pages.every((page) => page.reviewStage === 'confirmed');
  const here = `/homework/${encodeURIComponent(uploadId)}`;

  // Pages that can be checked, in order: "Page k of n" counts these.
  const readable = data.pages.filter((page) => page.reviewStage !== 'unreadable');
  const position = chosen === undefined ? -1 : readable.findIndex((page) => page.pageId === chosen.pageId);
  const next = position >= 0 ? readable[position + 1] : undefined;
  // #641: the page's one action leads on. Past the last readable page it is the feedback when every OTHER page is
  // already confirmed (this one is confirmed by the same action), and the summary otherwise.
  const othersConfirmed =
    data.pages.length === data.pageCount &&
    data.pages.every((page) => page.pageId === chosen?.pageId || page.reviewStage === 'confirmed');
  const feedbackTo = `${here}/feedback`;
  const forward =
    next !== undefined
      ? { to: `?page=${encodeURIComponent(next.pageId)}`, confirmLabel: t.confirmAndNext, forwardLabel: t.nextPage }
      : othersConfirmed
        ? { to: feedbackTo, confirmLabel: t.confirmAndFeedback, forwardLabel: t.seeFeedback }
        : { to: here, confirmLabel: t.confirmAndFinish, forwardLabel: t.finishCheck };

  // Pages that could not be read at all, named by number (the board's "We could not read page 9").
  const unreadable = data.pages.filter((page) => page.reviewStage === 'unreadable').map((page) => page.pageIndex + 1);
  const failures = anyReaderFailed ? (
    <section className="hw-failed" aria-labelledby={unreadable.length > 0 ? 'homework-page-failed' : undefined}>
      <span className="hw-failed-mark" aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v6" />
          <path d="M12 16.5v.5" />
        </svg>
      </span>
      <div className="hw-failed-text">
        {unreadable.length > 0 ? (
          <>
            <h2 id="homework-page-failed">{t.pagesNotReadTitle(unreadable)}</h2>
            <p>{t.pagesNotReadSub(unreadable.length, unreadable.length < data.pages.length)}</p>
          </>
        ) : (
          <p>{t.someReadersFailed}</p>
        )}
        <div className="hw-failed-reasons">
          <ReaderFailures readers={allReaders} />
        </div>
        <div className="hw-actions">
          <ReadAgainButton state={readAgain} label={t.retry} />
          <Link className="hw-secondary" to="/homework">
            {t.repostScan}
          </Link>
        </div>
      </div>
    </section>
  ) : null;
  const crumbs = <HomeworkCrumbs uploadId={uploadId} pageCount={Math.max(data.pageCount, data.pages.length)} />;

  return (
    <div className="page homework hw">
      {/* The board draws no title here (#648): the crumbs say where the learner is. The heading stays for the page's structure. */}
      {chosen === undefined ? null : <h1 className="visually-hidden">{t.homeworkHeading}</h1>}

      {upload.isError ? (
        <FailureNotice error={upload.error} onRetry={() => void upload.refetch()} />
      ) : null}

      {chosen !== undefined ? (
        <>
          {crumbs}
          <PageReview
            key={chosen.pageId}
            pageId={chosen.pageId}
            pageNumber={chosen.pageIndex + 1}
            readingAgain={readAgain.readingAgain}
            pageLabel={position >= 0 ? t.checkPage(position + 1) : t.pageN(chosen.pageIndex + 1)}
            summaryTo={here}
            forward={forward}
          />
        </>
      ) : data.state === 'failed' ? (
        <>
          {crumbs}
          <section className="hw-failed" role="alert">
            <span className="hw-failed-mark" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v6" />
                <path d="M12 16.5v.5" />
              </svg>
            </span>
            <div className="hw-failed-text">
              <h1>{t.fileNotReadHeading}</h1>
              <p>{t.uploadFailure(data.failureKind ?? 'unclassified')}</p>
              <p>{t.failedWhatToDo}</p>
              <div className="hw-actions">
                <Link className="hw-failed-primary" to="/homework">
                  {t.repostScan}
                </Link>
                <Link className="hw-secondary" to="/homework">
                  {t.backToHomework}
                </Link>
              </div>
            </div>
          </section>
        </>
      ) : reading ? (
        <div className="hw-reading-screen">
          {crumbs}
          <ReadingProgress
            pageCount={data.pageCount}
            estimate={estimateTimeLeft({ createdAt: data.createdAt, readAts: data.pages.map((page) => page.readAt), total: Math.max(data.pageCount, data.pages.length) })}
            tiles={data.pages.map((page) => ({
              pageId: page.pageId,
              pageNumber: page.pageIndex + 1,
              state: page.reviewStage === 'reading' ? 'reading' : page.reviewStage === 'unreadable' ? 'failed' : 'read',
            }))}
          />
        </div>
      ) : allConfirmed ? (
        <>
          {crumbs}
          <section className="hw-panel hw-done" aria-labelledby="homework-done">
            <h1 id="homework-done">{t.doneTitle}</h1>
            <p>{t.everyPageConfirmed}</p>
            <div className="hw-actions hw-actions-centre">
              <Link className="hw-panel-primary" to={`/homework/${encodeURIComponent(uploadId)}/feedback`}>
                {t.seeFeedback}
              </Link>
              <Link className="hw-panel-secondary" to="/">
                {t.homeShort}
              </Link>
            </div>
          </section>
        </>
      ) : data.pages.length === 0 ? (
        <>
          {crumbs}
          <h1>{t.homeworkHeading}</h1>
          <p>{t.noPagesYet}</p>
        </>
      ) : (
        <>
          {crumbs}
          <ReadySummary pages={data.pages} />
        </>
      )}

      {chosen === undefined && data.state !== 'failed' && !reading ? failures : null}
      {chosen === undefined && data.state === 'failed' && anyReaderFailed ? failures : null}
    </div>
  );
}
